import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const LANGUAGES = ["en", "de", "hu"];
const PROVIDERS = ["emergent", "openai", "anthropic", "google"];
const MODELS = ["gpt-5.4", "gpt-5.5", "claude-sonnet-4.6", "gemini-3.1-pro"];

export default function TenantSettings() {
  const { t } = useTranslation(["settings", "common"]);
  const { tenant, setTenant } = useAuth();

  const [name, setName] = useState(tenant?.name || "");
  const [language, setLanguage] = useState(tenant?.settings?.default_language || "en");
  const [provider, setProvider] = useState(tenant?.settings?.llm_provider || "emergent");
  const [model, setModel] = useState(tenant?.settings?.llm_model || "gpt-5.4");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.patch("/tenants/me", {
        name: name.trim(),
        default_language: language,
        llm_provider: provider,
        llm_model: model,
      });
      setTenant(data);
      toast.success(t("settings:tenant.saveSuccess"));
    } catch (err) {
      toast.error(formatApiError(err, t("common:errors.unexpected")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="tenant-settings-page">
      <header className="space-y-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Settings
        </span>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
          {t("settings:tenant.title")}
        </h1>
        <p className="max-w-xl text-sm text-slate-500">{t("settings:tenant.subtitle")}</p>
      </header>

      <form
        onSubmit={onSubmit}
        className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8"
        noValidate
      >
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="tenant-name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("settings:tenant.nameLabel")}
            </Label>
            <Input
              id="tenant-name"
              data-testid="tenant-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("settings:tenant.languageLabel")}
            </Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="tenant-language-select" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lng) => (
                  <SelectItem key={lng} value={lng} data-testid={`tenant-language-${lng}`}>
                    {t(`common:language.${lng}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("settings:tenant.providerLabel")}
            </Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger data-testid="tenant-provider-select" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`tenant-provider-${p}`}>
                    {t(`settings:tenant.providers.${p}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">{t("settings:tenant.providerHelp")}</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("settings:tenant.modelLabel")}
            </Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="tenant-model-select" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODELS.map((m) => (
                  <SelectItem key={m} value={m} data-testid={`tenant-model-${m}`}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">{t("settings:tenant.modelHelp")}</p>
          </div>
        </div>

        <div className="flex justify-end border-t border-slate-100 pt-6">
          <Button
            type="submit"
            disabled={submitting}
            data-testid="tenant-save-button"
            className="h-10 bg-slate-900 text-white hover:bg-slate-800"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" strokeWidth={1.75} /> {t("common:save")}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
